P<template>
  <v-dialog v-model="dialog" max-width="310" @click:outside="close">
    <v-card
      v-if="loading"
      height="580"
      class="d-flex justify-center align-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        class="loader"
      ></v-progress-circular>
    </v-card>
    <v-card
      v-else-if="bet !== null"
      class="bet-card"
      :height="partial ? 380 : 600"
    >
      <v-card-title class="headline">Bet Placement Details</v-card-title>
      <v-card-text class="bet-details">
        <div>
          <span class="b-title">Market</span>
          <span class="b-value">{{ bet.market.type }}</span>
        </div>
        <div>
          <span class="b-title">Stake Method</span>
          <span class="b-value">{{ bet.config.stake_method }}</span>
        </div>
        <div class="mt-2">
          <span class="b-title">Max Liability</span>
          <span class="b-value"
            >$ {{ bet.config.spreadsheet_liability || bet.mbl }}</span
          >
        </div>
        <div>
          <span class="b-title">Actual Liability</span>
          <span class="b-value"
            >$
            {{
              ((bet.prices.bookie - 1) * bet.stake.accepted).toFixed(2)
            }}</span
          >
        </div>
        <div>
          <span class="b-title">Stake:</span>
          <span class="b-value">$ {{ bet.stake.accepted }}</span>
        </div>
        <div v-if="bet.stake.accepted !== bet.stake.requested">
          <span class="b-title">Requested Stake:</span>
          <span class="b-value">$ {{ bet.stake.requested }}</span>
        </div>
        <div class="mt-2">
          <span class="b-title">Bookie Price:</span>
          <span class="b-value">$ {{ bet.prices.bookie.toFixed(2) }}</span>
        </div>
        <div>
          <span class="b-title">Betfair price:</span>
          <span class="b-value">$ {{ bet.prices.betfair.toFixed(2) }}</span>
        </div>
        <div class="mt-2">
          <span class="b-title">Target Profit Margin:</span>
          <span class="b-value"
            >{{ (bet.config.profit_margin * 100).toFixed(2) }} %</span
          >
        </div>
        <div>
          <span class="b-title">Expected Margin:</span>
          <span class="b-value"
            >{{
              ((bet.prices.bookie / bet.prices.betfair - 1) * 100).toFixed(2)
            }}
            %</span
          >
        </div>

        <div class="mt-2">
          <span class="b-title">Min Betfair Volume:</span>
          <span class="b-value"
            >$ {{ bet.config.betfair_volume.toFixed(2) }}</span
          >
        </div>
        <div>
          <span class="b-title">Profitable Volume:</span>
          <span class="b-value"
            >$ {{ bet.profitable_betfair_volume.toFixed(2) }}</span
          >
        </div>

        <div v-if="!partial" class="mt-4">
          <span class="b-extended-title">Bet Triggered</span>
          <div class="ladder-details">
            <span class="b-date">{{ triggeredDate }}</span>
            <span class="b-traded" v-if="bet.traded_volume.triggered"
              >Matched: $ {{ bet.traded_volume.triggered.toFixed(0) }}</span
            >
          </div>
          <div>
            <div
              :class="{
                ladder: true,
                'bf-first': true,
                'bf-type-back': true
              }"
            >
              <div class="bf-price">
                {{ bet.prices.bookie_triggered }}
              </div>
              <div class="bf-volume">${{ bet.config.betfair_volume }}</div>
            </div>

            <div
              v-for="(price,
              index) of bet.prices.betfair_ladder_triggered
                .filter(p => p[1])
                .slice(0, 6)"
              :key="index"
              :class="{
                ladder: true,
                'bf-first': index === 0,
                'bf-type-lay': true,
                'bf-type-back': false
              }"
            >
              <div class="bf-price">
                {{ price[0] }}
              </div>
              <div class="bf-volume">${{ price[1].toFixed(0) }}</div>
            </div>
          </div>
        </div>

        <template v-if="!partial">
          <div
            class="mt-6"
            v-for="submission in bet.submissions.slice().reverse()"
            :key="submission.attempt"
          >
            <span class="b-extended-title">
              Bet Submission {{ submission.attempt }}/{{
                bet.submissions.length
              }}
            </span>
            <div class="ladder-details">
              <span class="b-date">{{
                formatDate(submission.bet_submit_time)
              }}</span>
              <span class="b-traded" v-if="submission.traded_volume"
                >Matched: $ {{ submission.traded_volume.toFixed(0) }}</span
              >
            </div>
            <div>
              <div
                :class="{
                  ladder: true,
                  'bf-first': true,
                  'bf-type-back': true
                }"
              >
                <div class="bf-price">
                  {{ submission.price }}
                </div>
                <div class="bf-volume">${{ bet.config.betfair_volume }}</div>
              </div>

              <div
                v-for="(price, index) of submission.betfair_ladder
                  .filter(p => p[1])
                  .slice(0, 6)"
                :key="index"
                :class="{
                  ladder: true,
                  'bf-first': index === 0,
                  'bf-type-lay': true,
                  'bf-type-back': false
                }"
              >
                <div class="bf-price">
                  {{ price[0] }}
                </div>
                <div class="bf-volume">${{ price[1].toFixed(0) }}</div>
              </div>
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { getBet } from "../lib/bets";

export default {
  name: "BetPlacementDetailsDialog",
  props: ["betID", "dialog", "close"],
  data() {
    return {
      loading: false,
      bet: null,
      partial: false
    };
  },
  computed: {
    triggeredDate() {
      return moment(this.bet.dates.triggered).format("LTS");
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format("LTS");
    },
    async refresh() {
      this.loading = true;
      this.bets = null;
      this.partial = false;
      try {
        this.bet = await getBet(this.betID);
        this.partial = !this.bet.prices.betfair_ladder_triggered;
      } catch (err) {
        this.showError(`Unable to read bets- ${err}`);
      }

      this.loading = false;
    }
  },
  async mounted() {
    this.refresh();
  }
};
</script>

<style lang="scss" scoped>
.bet-card {
  overflow-y: scroll;
}

.b-title {
  display: inline-block;
  font-weight: 500;
  width: 160px;
}

.b-value {
  text-align: right;
}

.loader {
  margin: 0 auto;
  width: 100%;
}

.ladder-details {
  display: block;
  width: 100%;

  .b-date {
    font-size: 0.9em;
    margin-left: 8px;
  }

  .b-traded {
    font-size: 0.9em;
    margin-left: 8px;
    float: right;
    margin-right: 10px;
  }
}

.b-extended-title {
  display: inline-block;
  font-weight: 500;
}

.ladder {
  text-align: center;
  width: 37px;
  display: inline-block;

  .bf-price {
    width: 100%;
    font-weight: 700;
    display: inline-block;
    font-size: 0.85em;
  }

  .bf-volume {
    width: 100%;
    display: inline-block;
    font-size: 0.8em;
  }
}

.bf-first.bf-type-lay {
  background-color: #fac9d4;
}

.bf-first.bf-type-back {
  background-color: #a6d8ff;
}
</style>
